/* eslint-disable class-methods-use-this, max-lines */
import { EventBus, Events } from "jiffy-store";
import { isMobile } from "common/utils/browser";
import { notifyError } from "common/utils/app-monitoring/notify";
import "./product-color-palette.scss";

class ProductColorPalette {
  #colorItemClassName = ".js-product-color-palette-color-item";

  #activeItemClass = "js-color-variant-selector-active";

  #activeItemStyleClass = "product-color-palette__color-item--active";

  #hoverPreviewClass = "product-color-palette__color-item--hover-preview";

  #$root = null;

  #$colorItemActive = null;

  #$expandableContent = null;

  #$expandTrigger = null;

  #$lowStockBadge = null;

  #$materials = null;

  #$oosBadge = null;

  #$colorNameDisplay = null;

  #$headerBottom = null;

  #originalColorName = null;

  #isHovering = false;

  #hoverDebounceTimer = null;

  #$currentHoveredItem = null;

  // Fastest delivery sections state
  #$colorSectionsContainer = null;

  #$fastDeliverySection = null;

  #$fastDeliveryColors = null;

  #$otherColorsSection = null;

  #$otherColors = null;

  // Show All/Less state for Other Colors section
  #$otherColorsActions = null;

  #$otherColorsToggle = null;

  #$otherColorsToggleText = null;

  #$otherColorsToggleIcon = null;

  #isOtherColorsExpanded = false;

  #maxVisibleColors = 24;

  get #contentId() {
    const id = this.#$expandTrigger?.getAttribute("aria-controls");
    return `#${id}`;
  }

  constructor() {
    this.#$root = document.querySelector(".js-product-color-palette");
    this.#$colorItemActive = this.#$root?.querySelector(".js-color-variant-selector-active");
    this.#$expandTrigger = this.#$root?.querySelector(".js-product-color-palette-expand-trigger");
    this.#$expandableContent = this.#$root?.querySelector(this.#contentId);
    this.#$lowStockBadge = this.#$root?.querySelector(".js-color-palette-low-stock-badge");
    this.#$materials = this.#$root?.querySelector(".js-color-palette-materials");
    this.#$oosBadge = this.#$root?.querySelector(".js-color-palette-oos-badge");
    this.#$headerBottom = this.#$root?.querySelector(".js-product-color-palette__header-bottom");
    this.#$colorNameDisplay = this.#$root?.querySelector(".js-current-color-name");
    this.#originalColorName = this.#$colorNameDisplay?.textContent?.trim()?.toLowerCase();

    // Fastest delivery sections elements
    this.#$colorSectionsContainer = document.querySelector(".js-product-color-sections");
    this.#$fastDeliverySection = document.querySelector(".js-fast-delivery-section");
    this.#$fastDeliveryColors = document.querySelector(".js-fast-delivery-colors");
    this.#$otherColorsSection = document.querySelector(".js-other-colors-section");
    this.#$otherColors = document.querySelector(".js-other-colors");

    // Read max visible colors from data attribute (single source of truth from server)
    const maxVisibleFromData = this.#$colorSectionsContainer?.dataset?.maxVisibleColors;
    if (maxVisibleFromData) {
      this.#maxVisibleColors = parseInt(maxVisibleFromData, 10);
    }

    // Show All/Less elements
    this.#$otherColorsActions = document.querySelector(".js-other-colors-actions");
    this.#$otherColorsToggle = document.querySelector(".js-other-colors-toggle");
    this.#$otherColorsToggleText = document.querySelector(".js-toggle-text");
    this.#$otherColorsToggleIcon = document.querySelector(".js-toggle-icon");

    if (this.#$colorNameDisplay && this.#originalColorName) {
      this.#$colorNameDisplay.textContent = this.#originalColorName;
    }
    this.#updateHeaderBottomVisibility();
  }

  #updateHeaderBottomVisibility = () => {
    const isLowStockHidden = this.#$lowStockBadge?.classList.contains("hidden") ?? true;
    const isOosHidden = this.#$oosBadge?.classList.contains("hidden") ?? true;
    this.#$headerBottom?.classList.toggle("hidden", isLowStockHidden && isOosHidden);
  };

  #checkOverflow = () => {
    const hasOverflow =
      this.#$expandableContent.scrollHeight > this.#$expandableContent.clientHeight;
    this.#$expandTrigger.classList.toggle("hidden", !hasOverflow);
  };

  #isActiveElementVisible = () => {
    const containerRect = this.#$expandableContent.getBoundingClientRect();
    const containerTop = this.#$expandableContent.scrollTop;
    const containerBottom = containerTop + this.#$expandableContent.clientHeight;
    const elementRect = this.#$colorItemActive.getBoundingClientRect();
    const elementTop = elementRect.top - containerRect.top + this.#$expandableContent.scrollTop;
    const elementBottom = elementTop + elementRect.height;
    return elementTop >= containerTop && elementBottom <= containerBottom;
  };

  #isExpanded = () => this.#$expandTrigger.getAttribute("aria-expanded") === "true";

  #getMaterials = materialsRaw => {
    let materials = [];
    try {
      materials = JSON.parse(materialsRaw);
    } catch (error) {
      notifyError(`[JS] Failed to parse materials JSON: ${error.message}`, {
        // HB-1947: pin fingerprint — `${error.message}` interpolation would explode buckets per parse-error variant.
        fingerprint: "[JS] product-color-palette materials parse error",
        context: { materialsRaw }
      });
    }

    return materials.map(material => {
      const $material = document.createElement("div");
      $material.className = "product-color-palette__material";
      $material.textContent = material;
      return $material;
    });
  };

  #$getColorItemByCompositionId = compositionId =>
    this.#$root.querySelector(
      `${this.#colorItemClassName}[data-composition-id="${compositionId}"]`
    );

  #handleExpandTriggerClick = () => {
    const isExpanded = this.#isExpanded();

    this.#$expandTrigger.setAttribute("aria-expanded", !isExpanded);
    this.#$expandableContent.classList.toggle(
      "product-color-palette__content--expanded",
      !isExpanded
    );
  };

  #deactivateCurrentItem = () => {
    if (this.#$colorItemActive) {
      this.#$colorItemActive.classList.remove(this.#activeItemClass);
      this.#$colorItemActive.classList.remove(this.#activeItemStyleClass);
    }
  };

  #activateItem = $colorItem => {
    $colorItem.classList.add(this.#activeItemClass);
    $colorItem.classList.add(this.#activeItemStyleClass);
    this.#$colorItemActive = $colorItem;
  };

  #handleColorChange = ({ target: $target }) => {
    const $colorItem =
      $target.tagName === "A" ? $target : $target.closest(this.#colorItemClassName);

    const { color, lowStock, materials: materialsRaw, oos } = $colorItem.dataset;
    const materials = this.#getMaterials(materialsRaw);

    this.#$lowStockBadge.classList.toggle("hidden", lowStock !== "true");
    this.#$materials.replaceChildren(...materials);
    this.#$oosBadge.classList.toggle("hidden", oos !== "true");
    this.#updateHeaderBottomVisibility();

    if ($colorItem !== this.#$colorItemActive) {
      this.#deactivateCurrentItem();
      this.#activateItem($colorItem);
    }
    if (color) {
      this.#originalColorName = color.toLowerCase();
      if (!this.#$colorNameDisplay) return;
      this.#$colorNameDisplay.textContent = this.#originalColorName;
    }
  };

  #resetToOriginalState = () => {
    this.#$currentHoveredItem = null;
    this.#isHovering = false;
    if (this.#$colorNameDisplay && this.#originalColorName) {
      this.#$colorNameDisplay.textContent = this.#originalColorName;
    }
    EventBus.publish(Events.PRODUCT_COLOR_PREVIEW_END, this, {});
  };

  #applyPreviewState = $colorItem => {
    $colorItem.classList.add(this.#hoverPreviewClass);
    this.#$currentHoveredItem = $colorItem;
    this.#isHovering = true;

    const { color, compositionId, previewImageUrl } = $colorItem.dataset;

    if (this.#$colorNameDisplay && color) {
      this.#$colorNameDisplay.textContent = color.toLowerCase();
    }

    EventBus.publish(Events.PRODUCT_COLOR_PREVIEW, this, { color, compositionId, previewImageUrl });
  };

  #handleColorHover = e => {
    const $colorItem = e.target.closest(this.#colorItemClassName);
    if (!$colorItem || $colorItem === this.#$currentHoveredItem) return;

    const isActiveItem = $colorItem === this.#$colorItemActive;
    if (isActiveItem && !this.#$currentHoveredItem) return;

    if (this.#hoverDebounceTimer) {
      clearTimeout(this.#hoverDebounceTimer);
    }

    this.#hoverDebounceTimer = setTimeout(() => {
      if (this.#$currentHoveredItem) {
        this.#$currentHoveredItem.classList.remove(this.#hoverPreviewClass);
      }

      if (isActiveItem) {
        this.#resetToOriginalState();
        return;
      }

      this.#applyPreviewState($colorItem);
    }, 30);
  };

  #handlePaletteMouseLeave = () => {
    if (this.#hoverDebounceTimer) {
      clearTimeout(this.#hoverDebounceTimer);
      this.#hoverDebounceTimer = null;
    }
    if (this.#$currentHoveredItem) {
      this.#$currentHoveredItem.classList.remove(this.#hoverPreviewClass);
      this.#resetToOriginalState();
    }
  };

  #initHoverListeners() {
    if (isMobile()) return;
    const $colorItems = this.#$root?.querySelectorAll(this.#colorItemClassName);
    if (!$colorItems) return;
    $colorItems.forEach($item => $item.addEventListener("mouseenter", this.#handleColorHover));
    this.#$expandableContent?.addEventListener("mouseleave", this.#handlePaletteMouseLeave);
  }

  #initEventListeners() {
    this.#$expandTrigger.addEventListener("click", () => {
      this.#handleExpandTriggerClick();
    });
    EventBus.on(Events.PRODUCT_COLOR_SELECTED, (event, { compositionId }) => {
      if (this.#hoverDebounceTimer) {
        clearTimeout(this.#hoverDebounceTimer);
        this.#hoverDebounceTimer = null;
      }
      if (this.#$currentHoveredItem) {
        this.#$currentHoveredItem.classList.remove(this.#hoverPreviewClass);
        this.#$currentHoveredItem = null;
      }
      const $colorItem = this.#$getColorItemByCompositionId(compositionId);
      this.#handleColorChange({ target: $colorItem });
    });
    window.addEventListener("resize", () => {
      if (this.#isExpanded()) return;
      this.#checkOverflow();
    });
  }

  // Fastest Delivery Sections Methods
  #initFastestDeliverySections = () => {
    if (!this.#$colorSectionsContainer) return;

    // Server provides correct data - just initialize UI interactions
    this.#$expandableContent = this.#$colorSectionsContainer;
    this.#initHoverListenersForSections();
    this.#initOtherColorsToggle();
    this.#updateActiveItemReference(this.#$fastDeliveryColors);
    this.#updateActiveItemReference(this.#$otherColors);

    // Listen for zipcode changes to update color sections
    EventBus.on(Events.ZIPCODE_UPDATED, this.#handleZipcodeUpdate);
  };

  #handleZipcodeUpdate = async () => {
    const catalogNumber = this.#$colorSectionsContainer?.dataset?.catalogNumber;
    if (!catalogNumber) return;

    try {
      const response = await fetch(`/api/products/${catalogNumber}/fastest_delivery_stock`);
      if (!response.ok) return;

      const data = await response.json();
      const availableIds = data.enabled ? data.available_composition_ids || [] : [];
      this.#resortColorsByOptimalDc(new Set(availableIds));
    } catch (error) {
      // HB-1947: pin fingerprint — `${error.message}` interpolation would explode buckets per fetch-error variant.
      notifyError(`[JS] Failed to update colors on zipcode change: ${error.message}`, {
        fingerprint: "[JS] product-color-palette zipcode color update error"
      });
    }
  };

  #resortColorsByOptimalDc = availableCompositionIds => {
    // Get current items from both sections
    const $fastItems = this.#$fastDeliveryColors?.querySelectorAll(
      ".product-color-palette__list-item"
    );
    const $otherItems = this.#$otherColors?.querySelectorAll(".product-color-palette__list-item");

    // Collect all items and re-partition based on optimal DC data
    const allItems = [...Array.from($fastItems || []), ...Array.from($otherItems || [])];

    const fastDeliveryItems = [];
    const otherColorItems = [];

    allItems.forEach($item => {
      const compositionId = parseInt($item.dataset.compositionId, 10);
      $item.classList.remove("product-color-palette__list-item--collapsed");

      if (availableCompositionIds.has(compositionId)) {
        fastDeliveryItems.push($item);
      } else {
        otherColorItems.push($item);
      }
    });

    // Update sections with re-sorted items
    this.#updateSectionsWithItems(fastDeliveryItems, otherColorItems);
  };

  #updateSectionsWithItems = (fastDeliveryItems, otherColorItems) => {
    // Update fast delivery section
    if (fastDeliveryItems.length > 0) {
      this.#$fastDeliveryColors.replaceChildren(...fastDeliveryItems);
      this.#$fastDeliverySection.classList.remove("hidden");
    } else {
      this.#$fastDeliverySection.classList.add("hidden");
    }

    // Update other colors section
    if (otherColorItems.length > 0) {
      this.#$otherColors.replaceChildren(...otherColorItems);
      this.#$otherColorsSection.classList.remove("hidden");
      this.#applyOtherColorsVisibility();

      // Update section title
      const $title = this.#$otherColorsSection.querySelector(
        ".product-color-palette__section-title"
      );
      if ($title) {
        $title.textContent = fastDeliveryItems.length > 0 ? "All Other Colors" : "All Colors";
      }

      // Show/hide toggle button
      if (otherColorItems.length > this.#maxVisibleColors) {
        this.#$otherColorsActions?.classList.remove("hidden");
      } else {
        this.#$otherColorsActions?.classList.add("hidden");
      }
    } else {
      this.#$otherColorsSection.classList.add("hidden");
    }

    // Re-init hover listeners for moved items
    this.#initHoverListenersForSections();
    this.#updateActiveItemReference(this.#$fastDeliveryColors);
    this.#updateActiveItemReference(this.#$otherColors);
  };

  #initOtherColorsToggle = () => {
    if (!this.#$otherColorsToggle) return;
    this.#$otherColorsToggle.addEventListener("click", this.#handleOtherColorsToggle);
  };

  #updateActiveItemReference = $container => {
    if (!$container) return;
    const $activeClone = $container.querySelector(`.${this.#activeItemClass}`);
    if ($activeClone) {
      this.#$colorItemActive = $activeClone;
    }
  };

  #applyOtherColorsVisibility = () => {
    const $items = this.#$otherColors?.querySelectorAll(".product-color-palette__list-item");
    if (!$items) return;

    $items.forEach(($item, index) => {
      if (this.#isOtherColorsExpanded || index < this.#maxVisibleColors) {
        $item.classList.remove("product-color-palette__list-item--collapsed");
      } else {
        $item.classList.add("product-color-palette__list-item--collapsed");
      }
    });
  };

  #handleOtherColorsToggle = () => {
    this.#isOtherColorsExpanded = !this.#isOtherColorsExpanded;

    // Update button text and icon
    if (this.#$otherColorsToggleText) {
      this.#$otherColorsToggleText.textContent = this.#isOtherColorsExpanded
        ? "Show Less"
        : "Show All";
    }
    if (this.#$otherColorsToggle) {
      this.#$otherColorsToggle.setAttribute("aria-expanded", this.#isOtherColorsExpanded);
    }
    if (this.#$otherColorsToggleIcon) {
      this.#$otherColorsToggleIcon.classList.toggle(
        "product-color-palette__section-toggle-icon--expanded",
        this.#isOtherColorsExpanded
      );
    }

    // Apply visibility
    this.#applyOtherColorsVisibility();
  };

  #initHoverListenersForSections = () => {
    if (isMobile()) return;
    const $colorItems = this.#$colorSectionsContainer?.querySelectorAll(this.#colorItemClassName);
    if (!$colorItems) return;
    $colorItems.forEach($item => $item.addEventListener("mouseenter", this.#handleColorHover));
    this.#$fastDeliveryColors?.addEventListener("mouseleave", this.#handlePaletteMouseLeave);
    this.#$otherColors?.addEventListener("mouseleave", this.#handlePaletteMouseLeave);
  };

  init() {
    if (!this.#$root) return;

    this.#initEventListeners();

    // Initialize fastest delivery sections (server provides correct data, no async needed)
    if (this.#$colorSectionsContainer) {
      this.#initFastestDeliverySections();
    }

    this.#checkOverflow();
    this.#initHoverListeners();

    if (window.innerHeight >= 1000) this.#handleExpandTriggerClick();
    if (!this.#isActiveElementVisible()) this.#handleExpandTriggerClick();
  }
}

export default () => {
  const productColorPalette = new ProductColorPalette();
  productColorPalette.init();
};
